import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { Formik, type FormikHelpers } from 'formik';
import dayjs from 'dayjs';
import cn from 'classnames';
import { Loading } from 'pages';
import c from 'utils/constants';
import { useQueryParams } from 'hooks';

import { useGetPublicBookingEvent } from '../../hooks';
import ClientPublicBookingForm from './ClientPublicBookingForm';
import {
  CLIENT_BOOKING_STEPS,
  CLIENT_PUBLIC_BOOKING_FORM,
  CLIENT_PUBLIC_BOOKING_SCHEMA,
} from '../../forms/schema';
import ClientPublicBookingReview from './ClientPublicBookingReview';
import ClientPublicBookingSuccess from './ClientPublicBookingSuccess';
import ClientPublicBookingLinkExpired from './ClientPublicBookingLinkExpired';
import useCreatePublicBookingEvent from '../../hooks/useCreatePublicBookingEvent';

const FORM_INIT = {
  selectedDate: null,
  startAt: null,
};

export type PUBLIC_BOOKING_DETAILS = {
  title: string;
  content?: string | null;
  duration: number;
  eventUrl: string;
  emailAddress: string;
  startAt?: string;
  endAt?: string;
  guest?: Record<string, any> | null;
  eventId: string | null;
};

export default function ClientPublicBooking() {
  const [params] = useQueryParams();

  const { id } = useParams();
  const { data, isLoading, isError } = useGetPublicBookingEvent(
    id as string,
    params as Record<string, any>,
  );

  const { mutate: createBookingEvent } = useCreatePublicBookingEvent();
  const [bookingDetails, setBookingDetails] = useState<PUBLIC_BOOKING_DETAILS>({
    title: '',
    content: '',
    duration: 0,
    eventUrl: '',
    emailAddress: '',
    guest: null,
    eventId: '',
  });

  const [step, setStep] = useState<
    (typeof CLIENT_BOOKING_STEPS)[keyof typeof CLIENT_BOOKING_STEPS] | null
  >(null);

  useEffect(() => {
    if (isError) {
      setStep(CLIENT_BOOKING_STEPS.expired);
    }
  }, [isError]);

  useEffect(() => {
    if (data) {
      setStep(CLIENT_BOOKING_STEPS.form);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      const plainContent = new DOMParser().parseFromString(
        data?.content ?? '',
        'text/html',
      ).body.textContent;

      setBookingDetails((prevVal) => ({
        ...prevVal,
        ...data,
        content: plainContent,
      }));
    }
  }, [data]);

  const componentToRender = () => {
    switch (step) {
      case CLIENT_BOOKING_STEPS.form:
        return (
          <ClientPublicBookingForm publicBookingDetails={bookingDetails} />
        );
      case CLIENT_BOOKING_STEPS.review:
        return (
          <ClientPublicBookingReview
            setStep={setStep}
            publicBookingDetails={bookingDetails}
          />
        );
      case CLIENT_BOOKING_STEPS.success:
        return (
          <ClientPublicBookingSuccess publicBookingDetails={bookingDetails} />
        );
      case CLIENT_BOOKING_STEPS.expired:
        return <ClientPublicBookingLinkExpired />;
      default:
        return <Loading />;
    }
  };

  if (isLoading) return <Loading />;

  /**
   * Submits the form to create the client booking
   * @param {typeof FORM_INIT} value
   * @param {import('formik').FormikHelpers} formikHelpers
   * @return {void | Promise<any>}
   */
  const onSubmit = (
    value: CLIENT_PUBLIC_BOOKING_FORM,
    { setSubmitting }: FormikHelpers<CLIENT_PUBLIC_BOOKING_FORM>,
  ): void | Promise<any> => {
    if (step === CLIENT_BOOKING_STEPS.form) {
      setStep(CLIENT_BOOKING_STEPS.review);
      setSubmitting(false);
    } else if (step === CLIENT_BOOKING_STEPS.review) {
      setSubmitting(true);
      createBookingEvent(
        {
          id: id as string,
          payload: {
            date: dayjs
              .tz(
                dayjs(value.selectedDate)
                  .set('hour', +`${value.startAt ?? ''}`.slice(0, 2))
                  .set('minute', +`${value.startAt ?? ''}`.slice(2, 4)),
                c.DATE_TIME_FORMAT,
                import.meta.env.VITE_APP_TIMEZONE,
              )
              .toISOString(),
          },
        },
        {
          onSettled: () => setSubmitting(false),
          onError: () => setStep(CLIENT_BOOKING_STEPS.form),
          onSuccess({ data: res }) {
            setStep(CLIENT_BOOKING_STEPS.success);
            setBookingDetails((prevVal) => ({
              ...prevVal,
              eventUrl: res.eventUrl,
              eventId: res.id,
              startAt: res.startAt,
              endAt: res.endAt,
            }));
          },
        },
      );
    }
  };

  return (
    <div
      className={cn(
        'bg-[#F8FBFD]',
        'w-full',
        'grid',
        'place-items-center',
        'min-h-screen',
        'py-[45px]',
        'px-5',
      )}
    >
      <Formik<CLIENT_PUBLIC_BOOKING_FORM>
        enableReinitialize
        initialValues={FORM_INIT}
        onSubmit={onSubmit}
        validationSchema={CLIENT_PUBLIC_BOOKING_SCHEMA}
      >
        {componentToRender}
      </Formik>
    </div>
  );
}
